import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Save, Eye, ArrowLeft, Loader2 } from "lucide-react";

// Use relative URLs so API calls work on any domain (preview + production)
const API_URL = "";

const SKILL_COLORS = [
  { name: "blue", bg: "bg-blue-50", text: "text-blue-600" },
  { name: "rose", bg: "bg-rose-50", text: "text-rose-600" },
  { name: "amber", bg: "bg-amber-50", text: "text-amber-600" },
  { name: "emerald", bg: "bg-emerald-50", text: "text-emerald-600" },
  { name: "purple", bg: "bg-purple-50", text: "text-purple-600" },
  { name: "cyan", bg: "bg-cyan-50", text: "text-cyan-600" },
];

const emptyForm = {
  company_name: "",
  company_logo_url: "",
  sector: "",
  revenue: "",
  employees: "",
  stores_count: "",
  main_title: "",
  subtitle: "",
  context_title: "Il contesto del progetto",
  context_text: "",
  context_subtext: "",
  business_challenges: [{ title: "", description: "" }],
  hr_challenges: [{ title: "", description: "" }],
  objectives_title: "Cosa doveva cambiare",
  objectives: [{ text: "" }],
  solution_title: "Assessment AI con Skillvue",
  solution_subtitle: "",
  soft_skills: [{ name: "", color: "blue" }],
  solution_highlights: [{ title: "", description: "" }],
  result_metrics: [{ value: "", label: "", sublabel: "" }],
  qualitative_results: [{ title: "", description: "" }],
  vision_title: "Da sviluppo interno a talent strategy integrata",
  vision_subtitle: "",
  vision_objective_title: "Obiettivo strategico",
  vision_objective_subtitle: "",
  vision_points: [{ text: "" }],
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cases`);
      const data = await res.json();
      setCases(data);
    } catch (error) {
      console.error("Error fetching cases:", error);
    }
    setLoading(false);
  };

  const loadCase = async (slug) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cases/${slug}`);
      const data = await res.json();
      setFormData(data);
      setSelectedCase(slug);
    } catch (error) {
      console.error("Error loading case:", error);
    }
    setLoading(false);
  };

  const handleNewCase = () => {
    setFormData(emptyForm);
    setSelectedCase(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const url = selectedCase
        ? `${API_URL}/api/cases/${selectedCase}`
        : `${API_URL}/api/cases`;
      const method = selectedCase ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        const data = await res.json();
        setMessage({ type: "success", text: "Case study salvato con successo!" });
        setSelectedCase(data.slug);
        fetchCases();
      } else {
        const error = await res.json();
        setMessage({ type: "error", text: error.detail || "Errore nel salvataggio" });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Errore di connessione" });
    }
    setSaving(false);
  };

  const handleDelete = async (slug) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo case study?")) return;

    try {
      const res = await fetch(`${API_URL}/api/cases/${slug}`, { method: "DELETE" });
      if (res.ok) {
        fetchCases();
        if (selectedCase === slug) {
          handleNewCase();
        }
      }
    } catch (error) {
      console.error("Error deleting case:", error);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateArrayItem = (field, index, key, value) => {
    setFormData((prev) => {
      const arr = [...prev[field]];
      arr[index] = { ...arr[index], [key]: value };
      return { ...prev, [field]: arr };
    });
  };

  const addArrayItem = (field, template) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], template],
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Torna al sito
            </Button>
            <h1 className="text-xl font-bold text-slate-800">Admin - Case Studies</h1>
          </div>
          <div className="flex items-center gap-2">
            {selectedCase && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`/cases/${selectedCase}`, "_blank")}
              >
                <Eye className="w-4 h-4 mr-2" /> Anteprima
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salva
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 flex gap-8">
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-800">Case Studies</h2>
              <Button size="sm" variant="outline" onClick={handleNewCase}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {loading && !cases.length ? (
              <p className="text-sm text-slate-500">Caricamento...</p>
            ) : (
              <div className="space-y-2">
                {cases.map((c) => (
                  <div
                    key={c.slug}
                    className={`p-3 rounded-lg cursor-pointer flex items-center justify-between group ${
                      selectedCase === c.slug
                        ? "bg-[#4B4DF7]/10 border border-[#4B4DF7]/30"
                        : "bg-slate-50 hover:bg-slate-100"
                    }`}
                    onClick={() => loadCase(c.slug)}
                  >
                    <span className="text-sm font-medium truncate">{c.company_name}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(c.slug);
                      }}
                    >
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                ))}
                {!cases.length && (
                  <p className="text-sm text-slate-500">Nessun case study</p>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Main Form */}
        <div className="flex-1 space-y-6">
          {message.text && (
            <div
              className={`p-4 rounded-lg ${
                message.type === "success"
                  ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                  : "bg-red-50 text-red-800 border border-red-200"
              }`}
            >
              {message.text}
            </div>
          )}

          {/* Company Info */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Badge>1</Badge> Informazioni Azienda
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Nome Azienda *</label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => updateField("company_name", e.target.value)}
                  placeholder="Es: In's Mercato"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">URL Logo</label>
                <Input
                  value={formData.company_logo_url}
                  onChange={(e) => updateField("company_logo_url", e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Settore</label>
                <Input
                  value={formData.sector}
                  onChange={(e) => updateField("sector", e.target.value)}
                  placeholder="Es: GDO - Hard Discount"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Fatturato</label>
                <Input
                  value={formData.revenue}
                  onChange={(e) => updateField("revenue", e.target.value)}
                  placeholder="Es: 1,5 mld €"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Dipendenti</label>
                <Input
                  value={formData.employees}
                  onChange={(e) => updateField("employees", e.target.value)}
                  placeholder="Es: 4.200"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Punti Vendita</label>
                <Input
                  value={formData.stores_count}
                  onChange={(e) => updateField("stores_count", e.target.value)}
                  placeholder="Es: +570"
                />
              </div>
            </div>
          </Card>

          {/* Hero Section */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Badge>2</Badge> Hero Section
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Titolo Principale *</label>
                <Input
                  value={formData.main_title}
                  onChange={(e) => updateField("main_title", e.target.value)}
                  placeholder="Es: Come In's Mercato ha costruito una pipeline interna di Store Manager"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Sottotitolo</label>
                <Textarea
                  value={formData.subtitle}
                  onChange={(e) => updateField("subtitle", e.target.value)}
                  placeholder="Descrizione breve sotto il titolo"
                  rows={2}
                />
              </div>
            </div>
          </Card>

          {/* Context Section */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Badge>3</Badge> Contesto
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Titolo Sezione</label>
                <Input
                  value={formData.context_title}
                  onChange={(e) => updateField("context_title", e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Testo Principale</label>
                <Textarea
                  value={formData.context_text}
                  onChange={(e) => updateField("context_text", e.target.value)}
                  rows={4}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Testo Secondario</label>
                <Textarea
                  value={formData.context_subtext}
                  onChange={(e) => updateField("context_subtext", e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </Card>

          {/* Challenges */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Badge>4</Badge> Sfide
            </h3>

            {/* Business Challenges */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-slate-700">Sfide Business</label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => addArrayItem("business_challenges", { title: "", description: "" })}
                >
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
              <div className="space-y-3">
                {formData.business_challenges.map((ch, i) => (
                  <div key={i} className="flex gap-2 items-start bg-red-50/50 p-3 rounded-lg">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Titolo"
                        value={ch.title}
                        onChange={(e) => updateArrayItem("business_challenges", i, "title", e.target.value)}
                      />
                      <Textarea
                        placeholder="Descrizione"
                        value={ch.description}
                        onChange={(e) => updateArrayItem("business_challenges", i, "description", e.target.value)}
                        rows={2}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeArrayItem("business_challenges", i)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* HR Challenges */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-slate-700">Sfide HR</label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => addArrayItem("hr_challenges", { title: "", description: "" })}
                >
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
              <div className="space-y-3">
                {formData.hr_challenges.map((ch, i) => (
                  <div key={i} className="flex gap-2 items-start bg-amber-50/50 p-3 rounded-lg">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Titolo"
                        value={ch.title}
                        onChange={(e) => updateArrayItem("hr_challenges", i, "title", e.target.value)}
                      />
                      <Textarea
                        placeholder="Descrizione"
                        value={ch.description}
                        onChange={(e) => updateArrayItem("hr_challenges", i, "description", e.target.value)}
                        rows={2}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeArrayItem("hr_challenges", i)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Objectives */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Badge>5</Badge> Obiettivi
            </h3>
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700">Titolo Sezione</label>
              <Input
                value={formData.objectives_title}
                onChange={(e) => updateField("objectives_title", e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-slate-700">Lista Obiettivi</label>
              <Button
                size="sm"
                variant="outline"
                onClick={() => addArrayItem("objectives", { text: "" })}
              >
                <Plus className="w-4 h-4 mr-1" /> Aggiungi
              </Button>
            </div>
            <div className="space-y-2">
              {formData.objectives.map((obj, i) => (
                <div key={i} className="flex gap-2">
                  <Textarea
                    placeholder="Testo obiettivo"
                    value={obj.text}
                    onChange={(e) => updateArrayItem("objectives", i, "text", e.target.value)}
                    rows={2}
                    className="flex-1"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeArrayItem("objectives", i)}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </Card>

          {/* Solution */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Badge>6</Badge> Soluzione
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-sm font-medium text-slate-700">Titolo Sezione</label>
                <Input
                  value={formData.solution_title}
                  onChange={(e) => updateField("solution_title", e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Sottotitolo</label>
                <Input
                  value={formData.solution_subtitle}
                  onChange={(e) => updateField("solution_subtitle", e.target.value)}
                />
              </div>
            </div>

            {/* Soft Skills */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-slate-700">Soft Skills</label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => addArrayItem("soft_skills", { name: "", color: "blue" })}
                >
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                {formData.soft_skills.map((skill, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input
                      placeholder="Nome skill"
                      value={skill.name}
                      onChange={(e) => updateArrayItem("soft_skills", i, "name", e.target.value)}
                      className="flex-1"
                    />
                    <select
                      value={skill.color}
                      onChange={(e) => updateArrayItem("soft_skills", i, "color", e.target.value)}
                      className="px-2 py-2 border rounded-md text-sm"
                    >
                      {SKILL_COLORS.map((c) => (
                        <option key={c.name} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeArrayItem("soft_skills", i)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Solution Highlights */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-slate-700">Highlights</label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => addArrayItem("solution_highlights", { title: "", description: "" })}
                >
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
              <div className="space-y-3">
                {formData.solution_highlights.map((h, i) => (
                  <div key={i} className="flex gap-2 items-start bg-slate-50 p-3 rounded-lg">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Titolo"
                        value={h.title}
                        onChange={(e) => updateArrayItem("solution_highlights", i, "title", e.target.value)}
                      />
                      <Textarea
                        placeholder="Descrizione"
                        value={h.description}
                        onChange={(e) => updateArrayItem("solution_highlights", i, "description", e.target.value)}
                        rows={2}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeArrayItem("solution_highlights", i)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Results */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Badge>7</Badge> Risultati
            </h3>

            {/* Metrics */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-slate-700">Metriche Chiave</label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => addArrayItem("result_metrics", { value: "", label: "", sublabel: "" })}
                >
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {formData.result_metrics.map((m, i) => (
                  <div key={i} className="flex gap-2 items-start bg-emerald-50/50 p-3 rounded-lg">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Valore (es: 900)"
                        value={m.value}
                        onChange={(e) => updateArrayItem("result_metrics", i, "value", e.target.value)}
                      />
                      <Input
                        placeholder="Label (es: Assessment completati)"
                        value={m.label}
                        onChange={(e) => updateArrayItem("result_metrics", i, "label", e.target.value)}
                      />
                      <Input
                        placeholder="Sublabel (opzionale)"
                        value={m.sublabel}
                        onChange={(e) => updateArrayItem("result_metrics", i, "sublabel", e.target.value)}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeArrayItem("result_metrics", i)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Qualitative Results */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-slate-700">Risultati Qualitativi</label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => addArrayItem("qualitative_results", { title: "", description: "" })}
                >
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
              <div className="space-y-3">
                {formData.qualitative_results.map((r, i) => (
                  <div key={i} className="flex gap-2 items-start bg-slate-50 p-3 rounded-lg">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Titolo"
                        value={r.title}
                        onChange={(e) => updateArrayItem("qualitative_results", i, "title", e.target.value)}
                      />
                      <Textarea
                        placeholder="Descrizione"
                        value={r.description}
                        onChange={(e) => updateArrayItem("qualitative_results", i, "description", e.target.value)}
                        rows={2}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeArrayItem("qualitative_results", i)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Vision */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Badge>8</Badge> Visione
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-sm font-medium text-slate-700">Titolo Sezione</label>
                <Input
                  value={formData.vision_title}
                  onChange={(e) => updateField("vision_title", e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Sottotitolo</label>
                <Textarea
                  value={formData.vision_subtitle}
                  onChange={(e) => updateField("vision_subtitle", e.target.value)}
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Titolo Obiettivo</label>
                <Input
                  value={formData.vision_objective_title}
                  onChange={(e) => updateField("vision_objective_title", e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Sottotitolo Obiettivo</label>
                <Input
                  value={formData.vision_objective_subtitle}
                  onChange={(e) => updateField("vision_objective_subtitle", e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-slate-700">Punti Vision</label>
              <Button
                size="sm"
                variant="outline"
                onClick={() => addArrayItem("vision_points", { text: "" })}
              >
                <Plus className="w-4 h-4 mr-1" /> Aggiungi
              </Button>
            </div>
            <div className="space-y-2">
              {formData.vision_points.map((p, i) => (
                <div key={i} className="flex gap-2">
                  <Textarea
                    placeholder="Testo punto"
                    value={p.text}
                    onChange={(e) => updateArrayItem("vision_points", i, "text", e.target.value)}
                    rows={2}
                    className="flex-1"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeArrayItem("vision_points", i)}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end pb-8">
            <Button size="lg" onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salva Case Study
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
